import { cn } from "@/lib/utils";
import { StatusBadge, PaymentBadge, PayoutBadge, ConfirmPaymentButton } from "./TherapyBadges";
import { formatBookedAt, formatTime, dateParts, sessionShares } from "./helpers";
import { PersonAvatar } from "./TherapiesTable";
import { flagEmoji } from "@/lib/flag-emoji";
import { computeAdminPaymentBreakdown, formatMoney } from "@/lib/admin-payment-breakdown";
import type { Therapy } from "@/types";

export function TherapyCard({ therapy: t }: { therapy: Therapy }) {
  const breakdown = computeAdminPaymentBreakdown(t);
  const shares = sessionShares(t);
  const { day, month } = dateParts(t.scheduledAt);

  return (
    <div
      className={cn(
        "rounded-2xl border p-4 shadow-sm transition-shadow hover:shadow-md",
        t.paymentStatus === "pending_transfer"
          ? "border-amber-200 bg-amber-50/60"
          : "border-border/60 bg-card",
      )}
    >
      {/* Top row: date tile + time + status */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <div className="flex w-11 shrink-0 flex-col items-center rounded-xl border border-border/60 bg-muted/40 py-1.5 leading-none">
            <span
              className="text-base font-semibold text-foreground"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              {day}
            </span>
            <span className="mt-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              {month}
            </span>
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium text-foreground">
              {formatTime(t.scheduledAt)}
            </div>
            <div className="text-xs text-muted-foreground">{t.durationMinutes} min</div>
          </div>
        </div>
        <StatusBadge status={t.status} scheduledAt={t.scheduledAt} />
      </div>

      {/* People */}
      <div className="mt-3 space-y-2">
        <div className="flex items-start gap-2">
          <PersonAvatar name={t.clientName} tone="muted" className="size-7 text-[10px]" />
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-foreground">{t.clientName}</div>
            <div className="truncate text-xs text-muted-foreground">{t.clientEmail}</div>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <PersonAvatar name={t.therapistName} tone="primary" className="size-7 text-[10px]" />
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-foreground">{t.therapistName}</div>
            <div className="truncate text-xs text-muted-foreground">{t.therapistEmail}</div>
          </div>
        </div>
      </div>

      {/* Service */}
      {t.serviceName && (
        <div className="mt-2 truncate text-xs text-muted-foreground">{t.serviceName}</div>
      )}
      {t.createdAt && (
        <div className="mt-1 text-[11px] text-muted-foreground/70">
          Rezervisano {formatBookedAt(t.createdAt)}
        </div>
      )}

      <div className="mt-3 grid grid-cols-2 gap-2 border-t border-border/40 pt-3 text-xs">
        <MoneyCell label="Klijent plaća" value={shares.clientPays} country={t.bookingCountry} />
        {/* Kod sufinansiranja klijent uplaćuje samo svoj dio; ostatak je skinut
            sa kredita firme ili ide na njenu fakturu, pa se prikazuje odvojeno. */}
        {shares.cofunded && <MoneyCell label="Firma" value={shares.companyPays} muted />}
        <MoneyCell label="PDV" value={breakdown.vat} muted />
        <MoneyCell label="Platforma" value={breakdown.platformFee} muted />
        {/* Kod obveznika PDV-a "Terapeut" sadrži i njegov ulazni PDV, pa bez ove
            stavke prikazane cifre ne daju zbir (isti razlog kao tooltip u
            TherapiesTable). */}
        {breakdown.therapistVat !== null && breakdown.therapistVat > 0 && (
          <MoneyCell label="PDV terapeuta" value={breakdown.therapistVat} muted />
        )}
        <MoneyCell label="Terapeut" value={breakdown.therapistEarnings} />
      </div>

      <div className="mt-3 flex items-center justify-between gap-2 border-t border-border/40 pt-3">
        <div className="flex items-center gap-2.5">
          <PaymentBadge status={t.paymentStatus} />
          <PayoutBadge payoutId={t.payoutId} />
        </div>
        {t.paymentStatus === "pending_transfer" && <ConfirmPaymentButton sessionId={t.id} />}
      </div>
    </div>
  );
}

function MoneyCell({
  label,
  value,
  country,
  muted = false,
}: {
  label: string;
  value: number | null | undefined;
  country?: string | null;
  muted?: boolean;
}) {
  return (
    <div>
      <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={cn("mt-0.5 tabular-nums", muted ? "text-muted-foreground" : "font-semibold text-foreground")}>
        {formatMoney(value)}
        {value != null && <span className="ml-0.5 text-[10px] font-normal text-muted-foreground">KM</span>}
        {country && (
          <span className="ml-1 text-xs font-normal text-muted-foreground" title={country}>
            {flagEmoji(country)}
          </span>
        )}
      </div>
    </div>
  );
}
